import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core';
import { ethers } from 'ethers';
import {
  claimToken,
  createEntitlementChallenge,
  fetchMyTokenClaims,
  fetchPaymentProducts,
  updateClaim,
  verifyEntitlementChallenge,
} from './tokenIntelligenceApi';

const ERC20_ABI = [
  'function transfer(address to, uint256 amount) returns (bool)',
];

function getStoredClaimKey(address) {
  if (!address || typeof window === 'undefined' || !window.localStorage) return '';
  return String(window.localStorage.getItem(`e3d_claim_key:${String(address).toLowerCase()}`) || '');
}

function hasStoredClaimKey(address) {
  return !!getStoredClaimKey(address);
}

function setStoredClaimKey(address, apiKey) {
  if (!address || !apiKey || typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(`e3d_claim_key:${String(address).toLowerCase()}`, String(apiKey));
}

function buildSocials(values) {
  const out = {};
  for (const [key, value] of Object.entries(values || {})) {
    const normalized = String(value || '').trim();
    if (normalized) out[key] = normalized;
  }
  return out;
}

export default function ClaimTokenDialog({
  open,
  onClose,
  address,
  metadata,
  mode = 'claim',
  apiKey = '',
  onSaved,
}) {
  const existingClaim = metadata && metadata.claim ? metadata.claim : null;
  const [wallet, setWallet] = useState('');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [contact, setContact] = useState('');
  const [socialX, setSocialX] = useState('');
  const [socialTelegram, setSocialTelegram] = useState('');
  const [socialDiscord, setSocialDiscord] = useState('');
  const [socialGithub, setSocialGithub] = useState('');
  const [feeTxHash, setFeeTxHash] = useState('');
  const [paymentProducts, setPaymentProducts] = useState(null);
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [proofMethod, setProofMethod] = useState('owner_call');
  const [myClaims, setMyClaims] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const claimProduct = useMemo(() => {
    const products = paymentProducts && Array.isArray(paymentProducts.products) ? paymentProducts.products : [];
    return products.find((product) => product && product.product === 'claim') || null;
  }, [paymentProducts]);

  const paymentMethods = claimProduct && Array.isArray(claimProduct.paymentMethods) ? claimProduct.paymentMethods : [];
  const selectedMethod = paymentMethods.find((method) => method.id === paymentMethodId) || paymentMethods[0] || null;
  const effectiveApiKey = apiKey || getStoredClaimKey(address);

  useEffect(() => {
    if (!open) return;
    setWebsite(existingClaim && existingClaim.website ? existingClaim.website : (metadata && metadata.website ? metadata.website : ''));
    setDescription(existingClaim && existingClaim.description ? existingClaim.description : (metadata && metadata.description ? metadata.description : ''));
    setContact(existingClaim && existingClaim.contact ? existingClaim.contact : (metadata && metadata.contact ? metadata.contact : ''));
    setSocialX(existingClaim && existingClaim.socials && existingClaim.socials.x ? existingClaim.socials.x : '');
    setSocialTelegram(existingClaim && existingClaim.socials && existingClaim.socials.telegram ? existingClaim.socials.telegram : '');
    setSocialDiscord(existingClaim && existingClaim.socials && existingClaim.socials.discord ? existingClaim.socials.discord : '');
    setSocialGithub(existingClaim && existingClaim.socials && existingClaim.socials.github ? existingClaim.socials.github : '');
    setFeeTxHash('');
    setProofMethod('owner_call');
    setStatus('');
    setError('');
    setMyClaims([]);
  }, [open, existingClaim, metadata]);

  useEffect(() => {
    if (!open || mode === 'manage') return;
    let active = true;
    fetchPaymentProducts()
      .then((result) => {
        if (!active) return;
        setPaymentProducts(result);
        const products = result && Array.isArray(result.products) ? result.products : [];
        const claim = products.find((product) => product && product.product === 'claim');
        const methods = claim && Array.isArray(claim.paymentMethods) ? claim.paymentMethods : [];
        if (methods[0]) setPaymentMethodId((current) => current || methods[0].id);
      })
      .catch((err) => {
        if (!active) return;
        setError(err && err.message ? err.message : 'Failed to load claim payment configuration');
      });
    return () => { active = false; };
  }, [open, mode]);

  const verifyWalletSession = async (selectedWallet, signer, purpose) => {
    const challenge = await createEntitlementChallenge({
      wallet: selectedWallet,
      chain: 'ethereum',
      purpose,
    });
    const signature = await signer.signMessage(challenge.message);
    return verifyEntitlementChallenge({
      challengeId: challenge.challengeId,
      wallet: selectedWallet,
      chain: 'ethereum',
      message: challenge.message,
      signature,
    });
  };

  const handleConnectWallet = async () => {
    setError('');
    setStatus('');
    try {
      if (!window.ethereum) throw new Error('MetaMask is not installed');
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const selected = Array.isArray(accounts) && accounts[0] ? ethers.utils.getAddress(String(accounts[0])) : '';
      if (!selected) throw new Error('No wallet account is available');
      setWallet(selected);
      setStatus(`Connected wallet ${selected}`);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to connect wallet');
    }
  };

  const handleLoadMyClaims = async () => {
    setError('');
    setStatus('');
    setBusy(true);
    try {
      if (!wallet) throw new Error('Connect a wallet first');
      if (!window.ethereum) throw new Error('MetaMask is not installed');
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const signer = provider.getSigner();
      const verified = await verifyWalletSession(wallet, signer, 'token_claim_dashboard');
      const result = await fetchMyTokenClaims({
        wallet,
        chain: 'ethereum',
        sessionToken: verified && verified.sessionToken ? verified.sessionToken : '',
      });
      const claims = result && Array.isArray(result.claims) ? result.claims : [];
      setMyClaims(claims);
      setStatus(claims.length ? `Loaded ${claims.length} claimed token${claims.length === 1 ? '' : 's'}.` : 'No claimed tokens found for this wallet.');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load claimed tokens');
    } finally {
      setBusy(false);
    }
  };

  const handlePayFee = async () => {
    setError('');
    setStatus('');
    try {
      if (!selectedMethod) throw new Error('Claim payment method is unavailable');
      if (!window.ethereum) throw new Error('MetaMask is not installed');
      if (!wallet) throw new Error('Connect a wallet first');
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const network = await provider.getNetwork();
      if (Number(network.chainId) !== Number(selectedMethod.chainId)) {
        throw new Error(`Switch MetaMask to ${selectedMethod.network || selectedMethod.chain}`);
      }
      const signer = provider.getSigner();
      const token = new ethers.Contract(selectedMethod.tokenAddress, ERC20_ABI, signer);
      const amount = ethers.utils.parseUnits(String(claimProduct && claimProduct.claimFeeE3D ? claimProduct.claimFeeE3D : '0'), 18);
      setBusy(true);
      setStatus(`Submitting ${claimProduct && claimProduct.claimFeeE3D ? claimProduct.claimFeeE3D : '0'} ${selectedMethod.token} transfer...`);
      const tx = await token.transfer(selectedMethod.treasuryAddress, amount);
      const receipt = await tx.wait();
      setFeeTxHash(receipt && receipt.transactionHash ? receipt.transactionHash : tx.hash);
      setStatus(`Fee payment confirmed: ${receipt && receipt.transactionHash ? receipt.transactionHash : tx.hash}`);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to pay claim fee');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    setStatus('');
    setBusy(true);
    try {
      const socials = buildSocials({
        x: socialX,
        telegram: socialTelegram,
        discord: socialDiscord,
        github: socialGithub,
      });

      if (mode === 'edit') {
        if (!effectiveApiKey) throw new Error('No stored claim API key found for this token');
        const result = await updateClaim(address, {
          chain: 'ethereum',
          website,
          description,
          contact,
          socials,
        }, effectiveApiKey);
        setStatus('Claim updated.');
        if (typeof onSaved === 'function') onSaved({ ...result, apiKey: effectiveApiKey, website, description, contact, socials });
        return;
      }

      if (mode === 'manage') {
        await handleLoadMyClaims();
        return;
      }

      if (!wallet) throw new Error('Connect a wallet first');
      if (!feeTxHash) throw new Error('Pay the fee or paste the claim payment transaction hash');
      if (!window.ethereum) throw new Error('MetaMask is not installed');
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const signer = provider.getSigner();
      const verified = await verifyWalletSession(wallet, signer, 'token_claim');
      const result = await claimToken(address, {
        wallet,
        chain: 'ethereum',
        feeTxHash,
        paymentMethod: selectedMethod ? selectedMethod.id : '',
        paymentChain: selectedMethod ? selectedMethod.chain : '',
        proofMethod,
        website,
        description,
        contact,
        socials,
        sessionToken: verified.sessionToken,
      });
      if (result && result.apiKey) setStoredClaimKey(address, result.apiKey);
      setStatus('Token claimed.');
      if (typeof onSaved === 'function') onSaved({ ...result, website, description, contact, socials, wallet });
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to save claim');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {mode === 'edit' ? 'Edit Token Listing' : mode === 'manage' ? 'My Claimed Tokens' : 'Claim This Token'}
      </DialogTitle>
      <DialogContent dividers>
        <Box style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Typography variant="body2" color="textSecondary">
            {mode === 'edit'
              ? 'Update the owner-authored fields attached to this token.'
              : mode === 'manage'
                ? 'Review claims for this wallet and check whether each token still has a stored browser edit key.'
                : 'Pay the claim fee, then prove control with an owner()/deployer check or a wallet signature.'}
          </Typography>

          {mode !== 'edit' ? (
            <>
              <Box style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="outlined" color="primary" onClick={handleConnectWallet} disabled={busy}>
                  {wallet ? 'Wallet Connected' : 'Connect Wallet'}
                </Button>
                {wallet ? <Typography variant="body2">{wallet}</Typography> : null}
                {mode === 'manage' ? (
                  <Button variant="outlined" color="primary" onClick={handleLoadMyClaims} disabled={busy || !wallet}>
                    Load My Claims
                  </Button>
                ) : null}
              </Box>

              {mode !== 'manage' && claimProduct ? (
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8 }}>
                  <Typography variant="subtitle2">Claim fee</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {claimProduct.claimFeeE3D} {selectedMethod ? selectedMethod.token : 'E3D'} to {selectedMethod ? selectedMethod.treasuryAddress : 'configured treasury'}
                  </Typography>
                  {paymentMethods.length ? (
                    <TextField
                      select
                      SelectProps={{ native: true }}
                      label="Payment Method"
                      value={paymentMethodId}
                      onChange={(event) => setPaymentMethodId(event.target.value)}
                      variant="outlined"
                      size="small"
                    >
                      {paymentMethods.map((method) => (
                        <option key={method.id} value={method.id}>
                          {method.token} on {method.chain}
                        </option>
                      ))}
                    </TextField>
                  ) : null}
                  <Button variant="outlined" color="primary" onClick={handlePayFee} disabled={busy || !wallet || !selectedMethod}>
                    Pay Fee In Wallet
                  </Button>
                </Box>
              ) : null}

              {mode !== 'manage' ? (
                <>
                  <TextField
                    select
                    SelectProps={{ native: true }}
                    label="Proof Method"
                    value={proofMethod}
                    onChange={(event) => setProofMethod(event.target.value)}
                    fullWidth
                    variant="outlined"
                    helperText="Use owner() or deployer proof when your wallet matches the contract evidence. Signature remains available as a fallback."
                  >
                    <option value="owner_call">owner() proof</option>
                    <option value="deployer">Deployer proof</option>
                    <option value="signature">Signature-only proof</option>
                  </TextField>
                  <TextField
                    label="Fee Transaction Hash"
                    value={feeTxHash}
                    onChange={(event) => setFeeTxHash(event.target.value)}
                    fullWidth
                    variant="outlined"
                    helperText="Paste a confirmed ERC-20 transfer tx hash if you paid outside this dialog."
                  />
                </>
              ) : null}
            </>
          ) : null}

          {mode !== 'manage' ? (
            <>
              <TextField label="Website" value={website} onChange={(event) => setWebsite(event.target.value)} fullWidth variant="outlined" />
              <TextField label="Contact" value={contact} onChange={(event) => setContact(event.target.value)} fullWidth variant="outlined" />
              <TextField label="Description" value={description} onChange={(event) => setDescription(event.target.value)} fullWidth multiline rows={4} variant="outlined" />
              <TextField label="X" value={socialX} onChange={(event) => setSocialX(event.target.value)} fullWidth variant="outlined" />
              <TextField label="Telegram" value={socialTelegram} onChange={(event) => setSocialTelegram(event.target.value)} fullWidth variant="outlined" />
              <TextField label="Discord" value={socialDiscord} onChange={(event) => setSocialDiscord(event.target.value)} fullWidth variant="outlined" />
              <TextField label="GitHub" value={socialGithub} onChange={(event) => setSocialGithub(event.target.value)} fullWidth variant="outlined" />
            </>
          ) : null}

          {mode === 'manage' ? (
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {myClaims.map((claimItem) => (
                <Box
                  key={`${claimItem.chain}:${claimItem.address}`}
                  style={{
                    border: '1px solid rgba(0,0,0,0.12)',
                    borderRadius: 8,
                    padding: 12,
                    background: claimItem.address && String(claimItem.address).toLowerCase() === String(address || '').toLowerCase()
                      ? 'rgba(77,121,255,0.08)'
                      : 'transparent',
                  }}
                >
                  <Typography variant="subtitle2">{claimItem.address}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {claimItem.claimed ? 'Active claim' : 'Inactive claim'} · {claimItem.proofMethod}
                    {claimItem.stale ? ' · owner() changed' : ''}
                  </Typography>
                  {claimItem.claimedAt ? (
                    <Typography variant="body2" color="textSecondary">
                      Updated: {claimItem.claimedAt}
                    </Typography>
                  ) : null}
                  {claimItem.website ? (
                    <Typography variant="body2" color="textSecondary">
                      {claimItem.website}
                    </Typography>
                  ) : null}
                  <Typography variant="body2" color="textSecondary">
                    {hasStoredClaimKey(claimItem.address) ? 'Stored edit key available on this browser.' : 'No stored edit key found on this browser.'}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : null}

          {status ? <Typography style={{ color: '#2e7d32' }}>{status}</Typography> : null}
          {error ? <Typography style={{ color: '#c62828' }}>{error}</Typography> : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button color="primary" variant="contained" onClick={handleSubmit} disabled={busy}>
          {mode === 'edit' ? 'Save Changes' : mode === 'manage' ? 'Refresh List' : 'Claim Token'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
